import { useRef, useState } from 'react';
import styled from 'styled-components';

import Icon from '../../../components/Icon';
import IconButton from '../../../components/IconButton';
import NamedItem from '../../../components/NamedItem';
import useClickOutside from '../../../utils/useClickOutside';
import { partnersConfig } from '../../../utils/partners';
import { getCurrency } from '../../../utils/getCurrency';
import type { Locale, Partner } from '../../../client/portal/types.gen';

type PartnerMultiSelectProps = {
  partners: Partner[];
  // The partner names currently included; `null` means "all partners".
  selected: string[] | null;
  onChange: (selected: string[] | null) => void;
};

export const displayName = (partnerName: string): string =>
  partnersConfig[partnerName]?.name ?? partnerName;

// A partner's locale is its country; the per-country quick-select buttons
// are labelled with the country name.
const countryName: Record<Locale, string> = {
  DE: 'Germany',
  NO: 'Norway',
  SE: 'Sweden',
};

const PartnerMultiSelect = ({
  partners,
  selected,
  onChange,
}: PartnerMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => setOpen(false));

  const partnerNames = partners.map((partner) => partner.name);
  const effectiveSelection = selected ?? partnerNames;
  const allSelected = effectiveSelection.length === partnerNames.length;

  // One quick-select button per country present among the partners.
  const locales = Array.from(
    new Set(partners.map((partner) => partner.locale))
  ).sort((a, b) => countryName[a].localeCompare(countryName[b]));
  const partnersIn = (locale: Locale): string[] =>
    partners
      .filter((partner) => partner.locale === locale)
      .map((partner) => partner.name);
  // The country whose partners are exactly the current selection, if any.
  const activeLocale = locales.find((locale) => {
    const names = partnersIn(locale);
    return (
      names.length === effectiveSelection.length &&
      names.every((name) => effectiveSelection.includes(name))
    );
  });

  const count = `${effectiveSelection.length} of ${partnerNames.length} partners`;
  const summary =
    partners.length === 0
      ? 'Loading partners…'
      : allSelected
      ? `All partners (${partnerNames.length})`
      : activeLocale
      ? `${countryName[activeLocale]} (${count})`
      : count;

  const handleToggle = (partnerName: string) => {
    const withoutPartner = effectiveSelection.filter(
      (name) => name !== partnerName
    );
    const next = effectiveSelection.includes(partnerName)
      ? withoutPartner
      : [...effectiveSelection, partnerName];
    onChange(next.length === partnerNames.length ? null : next);
  };

  // Replace the selection with exactly this country's partners, normalised
  // to "all" when that happens to be every partner.
  const handleSelectCountry = (locale: Locale) => {
    const names = partnersIn(locale);
    onChange(names.length === partnerNames.length ? null : names);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <NamedItem>
        <label>Partners</label>
        <Value>
          {summary}
          <IconButton
            options={{ size: 'medium', backgroundColor: 'transparent' }}
            onClick={() => partners.length > 0 && setOpen(!open)}
          >
            <Icon iconName='faFilter' />
          </IconButton>
          {open && (
            <Dropdown>
              <QuickSelect>
                <SelectAllRow>
                  <SelectAllButton
                    type='button'
                    onClick={() => onChange(null)}
                    disabled={allSelected}
                  >
                    <Icon iconName='faSquareCheck' /> Select all
                  </SelectAllButton>
                  <SelectAllButton
                    type='button'
                    onClick={() => onChange([])}
                    disabled={effectiveSelection.length === 0}
                  >
                    <Icon iconName='faSquare' /> Unselect all
                  </SelectAllButton>
                </SelectAllRow>
                {locales.length > 0 && (
                  <CountryRow>
                    {locales.map((locale) => (
                      <CountryButton
                        key={locale}
                        type='button'
                        title={`Only partners in ${countryName[locale]}`}
                        aria-pressed={activeLocale === locale}
                        onClick={() => handleSelectCountry(locale)}
                      >
                        {countryName[locale]} ({partnersIn(locale).length})
                      </CountryButton>
                    ))}
                  </CountryRow>
                )}
              </QuickSelect>
              {partners.map((partner) => (
                <PartnerRow
                  key={partner.name}
                  onClick={() => handleToggle(partner.name)}
                >
                  <input
                    type='checkbox'
                    readOnly
                    checked={effectiveSelection.includes(partner.name)}
                  />
                  <PartnerLogo
                    src={
                      (partnersConfig[partner.name] ?? partnersConfig.default)
                        .logo
                    }
                    alt=''
                  />
                  {displayName(partner.name)}
                  <CurrencyBadge>{getCurrency(partner.locale)}</CurrencyBadge>
                </PartnerRow>
              ))}
            </Dropdown>
          )}
        </Value>
      </NamedItem>
    </Wrapper>
  );
};

export default PartnerMultiSelect;

const Wrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const Value = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  min-width: 240px;
`;

// Select all / unselect all plus the per-country buttons, above the list.
const QuickSelect = styled.div`
  border-bottom: 1px solid #dedede;
`;

const SelectAllRow = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
`;

const SelectAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #7b7d8c;
  padding: 0;

  &:hover:enabled {
    color: #fc8e49;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const CountryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  padding: 0 1rem 0.5rem;
`;

// A small pill; highlighted while the selection is exactly that country.
const CountryButton = styled(SelectAllButton)`
  border: 1px solid #dedede;
  border-radius: 1rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  white-space: nowrap;

  &:hover:enabled {
    border-color: #fc8e49;
  }

  &[aria-pressed='true'] {
    color: #fc8e49;
    border-color: #fc8e49;
    font-weight: 600;
  }
`;

const PartnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 1rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f5f5f5;
  }
`;

const PartnerLogo = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
`;

// Partners report in their own currency; show it where the selection is made.
const CurrencyBadge = styled.span`
  margin-left: auto;
  padding-left: 0.75rem;
  font-size: 0.75rem;
  color: #7b7d8c;
`;
